import React, {useEffect, useState} from 'react';

import {useHostSelector} from 'hooks/hostHooks/hostHooks';
import {localStore, sessionStore} from 'libs/storage/storage';
import {getRouterInfo} from 'selectors/router';

import {ProductDataType} from 'types/recommendationType';

type PropsType = {
    children: (args: ChildPropsType) => React.ReactElement;
    location?: string;
    customLocation?: string;
};

type ChildPropsType = {
    productViewList: ProductDataType[] | [];
    isLastElement: boolean;
    onProductView: (
        product: ProductDataType | ProductDataType[] | [],
        isLastProduct?: boolean,
        sku?: string,
    ) => void;
    currentLocation: string;
    recommendationItemClick: () => void;
};

export const RECOMMENDATION_JUMP_PAGE = 'recommendation_jump_page';
export const VIEWED_PRODUCT_LIST = 'list_of_viewed_recommended_products';
export const RECENTLY_VIEWED_PRODUCT_LIST = 'list_of_viewed_recently_viewed';
export const PDP_RECENTLY_VIEWED_PRODUCT_LIST = 'list_of_recently_viewed_on_pdp';
export const SIMILAR_FRAMES = 'list_of_viewed_similar_frames';
const HP_TOP_STRIP = 'HP Featured frames - Top Strip';
const HP_BOTTOM_STRIP = 'HP Featured frames - Bottom Strip';
const CTP_SIMILAR_FRAMES = 'Category - Recommendation - Similar Frames';
const PDP_RECOMMENDATION_RECENTLY_VIEWED = 'PDP - Recommendation - Recently Viewed';

const ENTITY_NAME_SESSION_STORAGE: {
    [key: string]:
        | typeof RECENTLY_VIEWED_PRODUCT_LIST
        | typeof VIEWED_PRODUCT_LIST
        | typeof SIMILAR_FRAMES
        | typeof PDP_RECENTLY_VIEWED_PRODUCT_LIST;
} = {
    [CTP_SIMILAR_FRAMES]: SIMILAR_FRAMES,
    [HP_BOTTOM_STRIP]: VIEWED_PRODUCT_LIST,
    [HP_TOP_STRIP]: RECENTLY_VIEWED_PRODUCT_LIST,
    [PDP_RECOMMENDATION_RECENTLY_VIEWED]: PDP_RECENTLY_VIEWED_PRODUCT_LIST,
};

/**
 * Will return a list of viewed products for analytics,
 * the identifier of the last product and the method of processing them
 * @return {ReturnType} data
 */
export const RecommendationProductView = ({children, customLocation = ''}: PropsType) => {
    const [productViewList, setProductsToList] = useState<ProductDataType[]>([]);
    const [isLastElement, setLastElement] = useState<boolean>(false);
    const [currentLocation, setCurrentLocation] = useState<string>('');
    const entityName = ENTITY_NAME_SESSION_STORAGE[customLocation] || VIEWED_PRODUCT_LIST;
    const location = useHostSelector(getRouterInfo).type;

    useEffect(() => {
        const formatLocation = location ? location[0].toUpperCase() + location.slice(1) : 'Cart';
        setCurrentLocation(customLocation || formatLocation);
    }, [customLocation, location]);

    /**
     * Writes the scanned accessory to the array
     * @param {ProductDataType | []} product - product data
     * @param {boolean} isLastProduct - return true if itemAccessory is the last one in the list
     * @param {string} sku - return product sku
     */
    const onProductView = (
        product: ProductDataType | ProductDataType[] | [],
        isLastProduct = false,
        sku = '',
    ) => {
        const productListFromSessionStorage = sessionStore.get(entityName, []) as string[];
        const isViewProduct = productListFromSessionStorage.some(
            productId => productId === (product as ProductDataType).product_id,
        );

        setLastElement(isLastProduct);

        if (!isViewProduct) {
            productListFromSessionStorage.push((product as ProductDataType).product_id);
            sessionStore.set(entityName, productListFromSessionStorage);

            setProductsToList((prevState: ProductDataType[] | []) =>
                Array.isArray(product)
                    ? product
                    : [...prevState, {...product, currentLocation, sku}],
            );
        }
    };

    /**
     * Will work when you click on the recommendation
     */
    const recommendationItemClick = () => localStore.set(RECOMMENDATION_JUMP_PAGE, currentLocation);

    return children({
        currentLocation,
        isLastElement,
        onProductView,
        productViewList,
        recommendationItemClick,
    });
};
